import { Skeleton } from '@/_components/ui/skeleton'
import type { WithAuthComponentProps } from '@/_hocs/with-auth'
import { WithAuth } from '@/_hocs/with-auth'
import { ContentContainer } from '@/features/_components/content-container'
import { Container } from '@/features/_components/ui/container'
import { useApplicationParams } from '@/features/_hooks/use-application-params'
import { queries } from '@/features/_lib/react-query'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useLocation } from '@tanstack/react-router'
import { PersonHeader } from '../_components/person-header'
import { PersonHeaderSkeleton } from '../_components/person-header.skeleton'
import { EditProvider } from '../_providers/edit'
import { FormProvider } from '../profile/_providers/form'
import { DataConfirmationContent } from './_components/data-confirmation-content'

const DataConfirmationPage = ({ session }: WithAuthComponentProps) => {
  const { companyId, facilityId, applicationId } = useApplicationParams()
  const { pathname } = useLocation()

  const { data, isLoading } = useQuery({
    ...queries.applications.detailFull(companyId, facilityId, applicationId),
    enabled: !!companyId && !!facilityId,
  })

  const { data: dataConfirmation, isLoading: isLoadingDataConfirmation } = useQuery({
    ...queries.staffApplicationCards.dataConfirmation(applicationId),
    enabled: !!applicationId,
  })

  const declarations = dataConfirmation?.declarations
  const officialData = dataConfirmation?.officialData ?? null

  const hasPendingDeclarations = (declarations ?? []).some((d) => d.status === 'pending')
  if (!isLoadingDataConfirmation && !hasPendingDeclarations) {
    return <Navigate to={pathname.replace(/\/data-confirmation$/, '/profile')} replace />
  }

  if (isLoading || !data || isLoadingDataConfirmation)
    return (
      <Container className="h-full overflow-hidden px-14 py-10 gap-6">
        <PersonHeaderSkeleton />
        <div className="scrollbar min-h-0 w-full flex-1 overflow-y-auto pb-10 pr-5">
          <ContentContainer className="gap-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-60 w-full" />
          </ContentContainer>
        </div>
      </Container>
    )

  const { application, facility } = data
  const person = application.person

  return (
    <Container className="h-full overflow-hidden px-14 py-10 gap-6">
      <EditProvider>
        <FormProvider person={person} application={application} facility={facility}>
          <PersonHeader
            application={application}
            person={person}
            facility={facility}
            userType={session.user.userType}
          />
        </FormProvider>
      </EditProvider>
      <div className="scrollbar min-h-0 w-full flex-1 overflow-y-auto pb-10 pr-5">
        <DataConfirmationContent
          declarations={declarations ?? []}
          officialData={officialData}
          isLoading={isLoadingDataConfirmation}
          application={application}
        />
      </div>
    </Container>
  )
}

export default WithAuth(DataConfirmationPage, { allowedFor: ['staff'] })
